import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import { useSession } from '../../../providers/session'
import ExistingUser from './existing'

type FormValues = {
  name: string
  lastName: string
  email: string
  phoneNumber: string
}

export const emailTestRegex =
  /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i

function setPhoneRef(e, state, setState, phoneInput) {
  // @ts-ignore
  if (window && window.intlTelInput && !state.iti) {
    try {
      // @ts-ignore
      const x = window.intlTelInput(e, {
        initialCountry: 'gb',
        utilsScript: '/tel.utils.min.js',
      })
      // @ts-ignore
      window.iti = x
      setState((s) => ({ ...s, iti: x }))
      return (phoneInput.current = e)
    } catch (err) {
      console.log(err)
    }
  }
}

export default forwardRef(function PersonalDetails(
  { profile, order }: { profile: any; order: any },
  ref
) {
  const [state, setState] = useState({
    iti: null,
    signIn: false,
  })
  // const { profile, refresh } = useSession()
  const phoneInput = useRef(null)
  const form = useForm<FormValues>({
    mode: 'all',
    reValidateMode: 'onChange',
    shouldUnregister: false,
  })

  const {
    formState: { errors, isValid },
    getValues,
    setValue,
    watch,
  } = form

  useImperativeHandle(
    ref,
    () => ({
      getValues() {
        const values = form.getValues()
        return values
      },
      async valid() {
        form.trigger()
        return isValid
      },
      trigger() {
        form.trigger()
      },
      setSignIn() {
        setState((s) => ({ ...s, signIn: true }))
      },
    }),
    [form, isValid]
  )

  const latestDetails = watch()
  order &&
    order.update({
      ...order.current,
      personalDetails: latestDetails,
    })

  const values = getValues()
  const existingClient =
    state.signIn || (errors.email && errors.email.type === 'exists')

  if (isValid) {
    order &&
      order.update({
        ...order.current,
        personalDetails: latestDetails,
      })
    // document.body.dispatchEvent(
    //   new CustomEvent('personal-details:changed', {
    //     detail: form.getValues(),
    //   })
    // )
  }

  const registerPhone = form.register('phoneNumber', {
    required: true,
    shouldUnregister: false,
    setValueAs: (v) => (state.iti ? state.iti.getNumber() : v),
    validate: {
      number: () => (state.iti ? state.iti.isValidNumber() : false),
    },
  })

  const registerEmail = form.register('email', {
    required: true,
    shouldUnregister: false,
    validate: {
      email: (v: string) => emailTestRegex.test(v),
      // exists: async (v: string) => {
      //   if (profile) return true;
      //   const req = await fetch(`/api/lookup/?email=${encodeURIComponent(v)}`)
      //   const res = await req.json()
      //   // @ts-ignore
      //   if (res && res.success) {
      //     return false
      //   }
      //   return true
      // }
    },
  })

  if (profile && profile.id && !values.email && !values.phoneNumber) {
    setValue('email', profile.email, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })
    setValue('phoneNumber', profile.phoneNumber, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })
    setValue('name', profile.name, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })
    setValue('lastName', profile.surname, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })
    form.trigger()
  }

  return (
    <section>
      <div
        className={`w-full py-4 text-left text-lg font-medium text-gray-800`}
      >
        <div className="flex items-center font-serif">
          <span
            className={`w-8 h-8 rounded-full text-center text-xs border-4 border-brand-600 text-brand-600 inline-flex items-center justify-center p-2 mr-2`}
          >
            1
          </span>
          <span className="flex-1">Personal Details</span>
          {/* {!state.signIn && !profile && (
            <button type='button' className='hidden md:block font-sans text-sm font-normal' onClick={() => setState(s => ({ ...s, signIn: true }))}>Already a member? <span className='font-medium'>Sign in</span></button>
          )} */}
        </div>

        <div className="pb-4">
          {!existingClient && (
            <div>
              <div className="mt-4 grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-4">
                <div>
                  <label
                    htmlFor="first-name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    First name
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="first-name"
                      {...form.register('name', {
                        required: true,
                        shouldUnregister: false,
                      })}
                      autoComplete="given-name"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 sm:text-sm text-gray-700"
                    />
                  </div>
                  {errors.name && (
                    <span className="error text-xs text-red-400 pb-2">
                      Name is required
                    </span>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="last-name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Last name
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="last-name"
                      {...form.register('lastName', {
                        required: true,
                        shouldUnregister: false,
                      })}
                      autoComplete="family-name"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 sm:text-sm text-gray-700"
                    />
                    {errors.lastName && (
                      <span className="error text-xs text-red-400 pb-2">
                        Last name is required
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Email
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="email"
                      {...registerEmail}
                      autoComplete="email"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 sm:text-sm text-gray-700"
                    />
                    {errors.email && errors.email.type === 'required' && (
                      <span className="error text-xs text-red-400 pb-2">
                        Email is required
                      </span>
                    )}
                    {errors.email && errors.email.type === 'email' && (
                      <span className="error text-xs text-red-400 pb-2">
                        Email address is invalid
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Phone
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="phone"
                      {...registerPhone}
                      ref={(e) => {
                        registerPhone.ref(e)
                        if (!state.iti) {
                          setPhoneRef(e, state, setState, phoneInput)
                        }
                      }}
                      autoComplete="tel"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 sm:text-sm text-gray-700"
                    />
                    {errors.phoneNumber &&
                      errors.phoneNumber.type === 'required' && (
                        <span className="error text-xs text-red-400 pb-2">
                          Phone is required
                        </span>
                      )}
                    {errors.phoneNumber &&
                      errors.phoneNumber.type === 'number' && (
                        <span className="error text-xs text-red-400 pb-2">
                          Phone number is invalid
                        </span>
                      )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {existingClient && (
            // @ts-ignore
            <ExistingUser
              // refresh={refresh}
              email={values.email}
              viaError={errors.email && errors.email.type === 'exists'}
            />
          )}
        </div>
      </div>
    </section>
  )
})
